import { Flex, Form, Input, Modal, Skeleton, Tag, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useRef, useState } from 'react';

import { useLazyGetLoyaltyCardsBySerialNumberV1Query } from '@app/api/ExternalLoyalty';
import type { LoyaltyCard } from '@app/api/ExternalLoyalty';
import type { AddNewLoyaltyCardModalProps } from '@app/components/RecordModal/components/RecordPayment/types';
import { RULES_IS_REQUIRE, SAVE } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { divideAmount } from '@app/utils';

const { Text } = Typography;

interface AddNewLoyaltyCardFormValues {
  serialNumber: string;
}

export const AddNewLoyaltyCardModal: React.FC<AddNewLoyaltyCardModalProps> = ({
  open,
  setIsOpen,
  setLoyaltyCards,
}) => {
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null
  );
  const isTimeoutStart = useRef(false);
  const [newCard, setNewCard] = useState<LoyaltyCard | null>(null);

  const [form] = useForm();

  const { showNotification } = useNotification();

  const [getLoyaltyCardsBySerialNumber, { isFetching }] =
    useLazyGetLoyaltyCardsBySerialNumberV1Query();

  const handleCancel = () => {
    setIsOpen(false);
    setNewCard(null);
    form.resetFields();
  };

  const handleSearchCard = async ({
    serialNumber,
  }: AddNewLoyaltyCardFormValues) => {
    try {
      const { cards } = await getLoyaltyCardsBySerialNumber({
        serialNumber: serialNumber.replace(/\s/g, ''),
      }).unwrap();
      isTimeoutStart.current = false;

      setNewCard(cards[0]);
    } catch (_) {
      setNewCard(null);
      showNotification(
        handleCatchError('Не удалось получить карту лояльности')
      );
    }
  };

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    form.setFieldsValue({ serialNumber: formatted });

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    isTimeoutStart.current = true;
    const timer = setTimeout(() => {
      handleSearchCard(form.getFieldsValue());
    }, 2000);

    setDebounceTimer(timer);
  };

  const handleSubmitForm = () => {
    setLoyaltyCards((prevCards) =>
      newCard ? [...prevCards, newCard] : prevCards
    );
    handleCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title="Добавление сторонней карты лояльности"
      okText={SAVE}
      onOk={() => form.submit()}
    >
      <Form form={form} onFinish={handleSubmitForm}>
        <Flex vertical gap={16}>
          <Flex vertical gap={10}>
            <Text strong className="label-require">
              Введите номер карты лояльности
            </Text>
            <Form.Item rules={RULES_IS_REQUIRE} name="serialNumber">
              <Input
                inputMode="numeric"
                pattern="\d*"
                maxLength={19}
                onChange={handleInputChange}
              />
            </Form.Item>
          </Flex>
          {(isFetching || isTimeoutStart.current) && <Skeleton active />}
          {newCard && (
            <>
              <Flex justify="space-between" gap={16}>
                <Text>Баланс</Text>
                <Text strong>{divideAmount(newCard.bonuses ?? 0)} бонусов</Text>
              </Flex>
              <Text strong>Данные карты:</Text>
              <Flex justify="space-between" gap={16}>
                <Text>Название</Text>
                <Text strong>{divideAmount(newCard.name ?? 0)} бонусов</Text>
              </Flex>
              <Flex justify="space-between" gap={16}>
                <Text>Скидка</Text>
                <Text strong>
                  {divideAmount(newCard.discount ?? 0)} бонусов
                </Text>
              </Flex>
              <Flex justify="space-between" gap={16}>
                <Text>Уровень</Text>
                <Tag color="silver">{newCard.level ?? 'Неизвестно'}</Tag>
              </Flex>
            </>
          )}
        </Flex>
      </Form>
    </Modal>
  );
};
